import { FC, useMemo, useState } from 'react';
import {
  Autocomplete,
  Avatar,
  Box,
  Chip,
  TextField,
  Typography,
  createFilterOptions,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import { useSnackbar } from 'notistack';
import {
  errorMessages,
  successMessages,
  validationMessages,
} from 'data/messages/messages';
import {
  useGetAllUsersBaseInfoQuery,
  useInviteUserMutation,
} from 'redux/apiSlices/authUser.slice';
import { IAddProjectMemberUI, IProjectUI } from 'data/types/project.types';
import { useAddProjectMembersMutation } from 'redux/apiSlices/project.slice';
import { IUserBaseInfoUI } from 'data/types/user.types';
import { inviteProjectMemberSchema } from 'data/validation/ProjectSchemas';
import { TRtkErrorType } from 'data/types/general.types';
import Button from 'components/Base/Button';

type TAddProjectMemberProps = {
  currentMembers: IProjectUI['members'];
  projectId: string;
  projectName: string;
};

const filterOptions = createFilterOptions({
  matchFrom: 'any',
  stringify: (option: IUserBaseInfoUI) => `${option.email} ${option.name}`,
  ignoreCase: true,
  trim: true,
});

const AddProjectMember: FC<TAddProjectMemberProps> = ({
  currentMembers,
  projectId,
  projectName,
}) => {
  const [selectedValues, setSelectedValues] = useState<IUserBaseInfoUI[]>([]);
  const [inputValue, setInputValue] = useState('');

  const hasSelectedValues = useMemo(
    () => !!selectedValues.length,
    [selectedValues],
  );

  const { data, isLoading } = useGetAllUsersBaseInfoQuery();
  const [addProjectMembers, { isLoading: addMemberLoading }] =
    useAddProjectMembersMutation();
  const [inviteUsers, { isLoading: inviteUsersLoading }] =
    useInviteUserMutation();

  const { enqueueSnackbar } = useSnackbar();

  const onAddNewMembers = async () => {
    const membersEmailsToSendIvitation: string[] = [];
    const membersExistsInSystem: IAddProjectMemberUI[] = [];

    selectedValues.forEach(member => {
      if (member.id) {
        membersExistsInSystem.push(member);
      } else {
        membersEmailsToSendIvitation.push(member.email);
      }
    });

    const userEmailsFailedToInvite: string[] = [];

    if (membersEmailsToSendIvitation.length) {
      try {
        const response = await inviteUsers({
          emails: membersEmailsToSendIvitation,
        }).unwrap();
        const usersSuccessfullyInvited: IAddProjectMemberUI[] = [];
        response.forEach(res => {
          if (res.status === 'Success' && res.id) {
            usersSuccessfullyInvited.push({
              email: res.email,
              id: res.id,
              name: res.name || '',
            });
          } else {
            userEmailsFailedToInvite.push(res.email);
          }
        });

        membersExistsInSystem.push(...usersSuccessfullyInvited);
      } catch (error) {
        const e = error as TRtkErrorType;
        enqueueSnackbar(e?.data.message || errorMessages.default, {
          variant: 'error',
        });
      }
    }

    if (!membersExistsInSystem.length) {
      return;
    }

    try {
      await addProjectMembers({
        newMembers: membersExistsInSystem,
        projectId,
      }).unwrap();
      enqueueSnackbar(successMessages.toast, {
        variant: 'success',
      });
    } catch (err) {
      const error = err as TRtkErrorType;
      enqueueSnackbar(error?.data?.message || errorMessages.default, {
        variant: 'error',
      });
    }

    if (userEmailsFailedToInvite.length) {
      enqueueSnackbar(
        `Failed to invite ${userEmailsFailedToInvite.join(', ')}`,
        {
          variant: 'error',
        },
      );
    }

    setSelectedValues([]);
  };

  const onAddNewUserInList = () => {
    const isEmailValid = inviteProjectMemberSchema.isValidSync({
      email: inputValue,
    });
    if (!isEmailValid) {
      enqueueSnackbar(validationMessages.email, {
        variant: 'error',
      });
      return;
    }
    setSelectedValues(prev => [
      ...(prev || []),
      { email: inputValue, id: '', name: '' },
    ]);
    setInputValue('');
  };

  const options = useMemo(() => {
    return (data || [])?.filter(
      user =>
        ![...(selectedValues || []), ...currentMembers].find(
          member => member.email === user.email,
        ),
    );
  }, [data, currentMembers, selectedValues]);

  return (
    <Box>
      <Autocomplete
        value={selectedValues}
        inputValue={inputValue}
        options={options}
        filterOptions={filterOptions}
        fullWidth
        multiple
        popupIcon={null}
        open={!!inputValue}
        renderInput={params => (
          <TextField
            {...params}
            variant="outlined"
            placeholder="Search by name or email..."
            size="small"
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  </InputAdornment>
                  {params.InputProps.startAdornment}
                </>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'background.paper',
              },
            }}
          />
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => {
            const isNewUser = !option.id;
            const initials = option.name
              ? option.name
                  .split(' ')
                  .map(n => n[0])
                  .join('')
                  .toUpperCase()
                  .slice(0, 2)
              : option.email[0].toUpperCase();

            return (
              <Chip
                label={option.email}
                avatar={
                  isNewUser ? (
                    <Avatar sx={{ bgcolor: 'primary.light' }}>
                      <EmailOutlinedIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                    </Avatar>
                  ) : (
                    <Avatar
                      sx={{
                        bgcolor: option.avatarColor || 'primary.main',
                        fontSize: '0.7rem',
                      }}
                    >
                      {initials}
                    </Avatar>
                  )
                }
                size="small"
                {...getTagProps({ index })}
                sx={{
                  borderRadius: 2,
                  '& .MuiChip-label': {
                    fontWeight: 500,
                  },
                }}
              />
            );
          })
        }
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue);
        }}
        renderOption={(props, optionValue) => {
          const initials = optionValue.name
            ? optionValue.name
                .split(' ')
                .map(n => n[0])
                .join('')
                .toUpperCase()
                .slice(0, 2)
            : optionValue.email[0].toUpperCase();

          return (
            <Box
              component="li"
              {...props}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                py: 1,
              }}
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: optionValue.avatarColor || 'primary.main',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                }}
              >
                {initials}
              </Avatar>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {optionValue.name || optionValue.email.split('@')[0]}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {optionValue.email}
                </Typography>
              </Box>
            </Box>
          );
        }}
        loading={isLoading}
        disabled={inviteUsersLoading}
        noOptionsText={
          <Box
            onClick={onAddNewUserInList}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              py: 1,
              px: 0.5,
              cursor: 'pointer',
              borderRadius: 1,
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: 'primary.light',
              }}
            >
              <PersonAddIcon sx={{ fontSize: 16, color: 'primary.main' }} />
            </Avatar>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Invite "{inputValue}"
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Send invitation to join {projectName}
              </Typography>
            </Box>
          </Box>
        }
        onChange={async (event, newValue) => {
          setSelectedValues(newValue);
        }}
        getOptionLabel={option => option.email}
      />

      {hasSelectedValues && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mt: 2,
          }}
        >
          <Button
            variant="contained"
            loading={addMemberLoading}
            onClick={onAddNewMembers}
            startIcon={<PersonAddIcon />}
          >
            Add {selectedValues.length} {selectedValues.length === 1 ? 'person' : 'people'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default AddProjectMember;
